import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/ai',
  component: AiSettings,
})

const PROVIDERS = [
  { value: 'none', label: 'Keine KI' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai-compatible', label: 'OpenAI-kompatibel (vLLM, Ollama, Azure)' },
]

const PROVIDER_DEFAULTS: Record<string, { url: string; model: string }> = {
  anthropic: { url: 'https://api.anthropic.com', model: 'claude-sonnet-4-6' },
  'openai-compatible': { url: 'http://localhost:8000/v1', model: 'gemma-4-31b' },
}

function AiSettings() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const qc = useQueryClient()

  const { data: aiConfig } = useQuery({
    queryKey: ['ai', 'config'],
    queryFn: () => api.getAiConfig(),
    enabled: !!meData?.user,
  })

  const [provider, setProvider] = useState('none')
  const [apiUrl, setApiUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  // GET /ai/config never returns the stored custom prompt, so the field starts
  // empty. Track whether the admin actually edited it — otherwise "Speichern"
  // (e.g. after only changing the model) would send an empty prompt and wipe
  // the previously configured one from the DB.
  const [systemPromptTouched, setSystemPromptTouched] = useState(false)
  const [sttProvider, setSttProvider] = useState('none')
  const [sttApiUrl, setSttApiUrl] = useState('')
  const [sttApiKey, setSttApiKey] = useState('')
  const [sttModel, setSttModel] = useState('')
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle')

  useEffect(() => {
    if (aiConfig) {
      setProvider(aiConfig.provider)
      setModel(aiConfig.model ?? '')
      setSttProvider(aiConfig.stt?.provider ?? 'none')
      setSttModel(aiConfig.stt?.model ?? '')
    }
  }, [aiConfig])

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateAiConfig({
        aiProvider: provider,
        aiApiUrl: apiUrl || undefined,
        aiApiKey: apiKey || undefined,
        aiModel: model || undefined,
        // Only send the prompt when the admin edited it — omitting it leaves
        // the stored value untouched instead of clearing it.
        ...(systemPromptTouched ? { aiSystemPrompt: systemPrompt || null } : {}),
        sttProvider,
        sttApiUrl: sttApiUrl || undefined,
        sttApiKey: sttApiKey || undefined,
        sttModel: sttModel || undefined,
      }),
    onSuccess: () => {
      notify.success('KI-Einstellungen gespeichert')
      qc.invalidateQueries({ queryKey: ['ai', 'config'] })
      setApiKey('')
      setSttApiKey('')
    },
    onError: () => notify.error('Fehler beim Speichern'),
  })

  const handleProviderChange = (val: string) => {
    setProvider(val)
    const defaults = PROVIDER_DEFAULTS[val]
    if (defaults) {
      setApiUrl(defaults.url)
      setModel(defaults.model)
    } else {
      setApiUrl('')
      setModel('')
    }
  }

  const handleTest = async () => {
    setTestStatus('testing')
    try {
      const res = await api.aiChat([
        { role: 'user', content: 'Antworte mit "OK" und sonst nichts.' },
      ])
      setTestStatus(res.events.some((e) => e.type === 'text') ? 'ok' : 'fail')
    } catch {
      setTestStatus('fail')
    }
  }

  if (meLoading) return null
  if (meData?.user?.role !== 'admin') return <Navigate to="/settings/profile" />

  return (
    <SettingsLayout
      title="KI-Assistent"
      description="Konfiguriere den KI-Assistenten für alle Benutzer."
    >
      <div className="space-y-6 max-w-xl">
        {/* Provider */}
        <div className="space-y-2">
          <Label>Provider</Label>
          <select
            value={provider}
            onChange={(e) => handleProviderChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface-secondary text-text-primary px-3 py-2 text-base lg:text-sm"
          >
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        {provider !== 'none' && (
          <>
            {/* API URL */}
            <div className="space-y-2">
              <Label>API-URL</Label>
              <Input
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://api.anthropic.com"
              />
            </div>

            {/* API Key */}
            <div className="space-y-2">
              <Label>API-Key</Label>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={aiConfig?.configured ? '••••••••  (gesetzt)' : 'sk-...'}
              />
              <p className="text-xs text-text-tertiary">
                Wird verschlüsselt gespeichert. Für lokale vLLM-Instanzen kann das Feld leer
                bleiben.
              </p>
            </div>

            {/* Model */}
            <div className="space-y-2">
              <Label>Modell</Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="claude-sonnet-4-6"
              />
            </div>

            {/* Custom System Prompt */}
            <div className="space-y-2">
              <Label>Custom System Prompt (optional)</Label>
              <textarea
                value={systemPrompt}
                onChange={(e) => {
                  setSystemPrompt(e.target.value)
                  setSystemPromptTouched(true)
                }}
                rows={3}
                placeholder="Zusätzliche Anweisungen für den Assistenten…"
                className="w-full rounded-lg border border-border bg-surface-secondary text-text-primary px-3 py-2 text-base lg:text-sm placeholder:text-text-muted resize-y"
              />
            </div>
          </>
        )}

        {/* Speech-to-Text (Sprachnachrichten-Transkription) */}
        <div className="space-y-2 border-t border-border-subtle pt-5">
          <Label>Sprachnachrichten-Transkription (STT)</Label>
          <p className="text-xs text-text-tertiary">
            OpenAI-kompatibler Endpoint (<code>/v1/audio/transcriptions</code>, z. B.
            faster-whisper-server). Transkribiert Chat-Sprachnachrichten und aktiviert die
            Spracheingabe im KI-Assistenten.
          </p>
          <select
            value={sttProvider}
            onChange={(e) => setSttProvider(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface-secondary text-text-primary px-3 py-2 text-base lg:text-sm"
          >
            <option value="none">Deaktiviert</option>
            <option value="openai-compatible">OpenAI-kompatibel (Whisper)</option>
          </select>
        </div>

        {sttProvider !== 'none' && (
          <>
            <div className="space-y-2">
              <Label>STT-API-URL</Label>
              <Input
                value={sttApiUrl}
                onChange={(e) => setSttApiUrl(e.target.value)}
                placeholder="http://whisper.embertime.svc:8000"
              />
              <p className="text-xs text-text-tertiary">
                Beim Ändern der URL ohne neuen Key wird der gespeicherte Key aus Sicherheitsgründen
                verworfen.
              </p>
            </div>
            <div className="space-y-2">
              <Label>STT-API-Key (optional)</Label>
              <Input
                type="password"
                value={sttApiKey}
                onChange={(e) => setSttApiKey(e.target.value)}
                placeholder={
                  aiConfig?.stt?.configured ? '••••••••  (gesetzt)' : 'leer für lokale Instanzen'
                }
              />
            </div>
            <div className="space-y-2">
              <Label>STT-Modell</Label>
              <Input
                value={sttModel}
                onChange={(e) => setSttModel(e.target.value)}
                placeholder="Systran/faster-whisper-small"
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Speichern …' : 'Speichern'}
          </Button>

          {provider !== 'none' && aiConfig?.configured && (
            <Button variant="outline" onClick={handleTest} disabled={testStatus === 'testing'}>
              {testStatus === 'testing' && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {testStatus === 'ok' && <CheckCircle className="h-4 w-4 mr-1.5 text-green-400" />}
              {testStatus === 'fail' && <XCircle className="h-4 w-4 mr-1.5 text-red-400" />}
              Verbindung testen
            </Button>
          )}
        </div>
      </div>
    </SettingsLayout>
  )
}
